<template>
  <Transition name="fade" mode="out-in">
    <CCard class="!p-0 md:!px-4 md:!pt-4">
      <template v-if="isMobile">
        <div>
          <CTableHeader
            :title="$t('performance')"
            no-search
            class="!flex-row justify-between p-4 md:p-5"
          >
            <template #afterSearch>
              <template v-if="bots?.length">
                <FSelect
                  :options="bots"
                  v-model="botId"
                  label-key="strategy"
                  value-key="id"
                  :placeholder="$t('choose_bot')"
                />
              </template>
            </template>
          </CTableHeader>
          <template v-if="botPer?.length">
            <div class="gap-3">
              <CPerformanceCard
                class="p-4 md:p-5"
                :data="card"
                v-for="(card, idx) of botPer"
                :key="idx"
              />
            </div>
          </template>
          <template v-else>
            <CNodata
              main-class="min-h-[202px] max-w-[300px]"
              :title="$t('no_bots')"
              :subtitle="$t('no_bots_text')"
              image="/images/svg/no-data/no-bots.svg"
            />
          </template>
        </div>
      </template>
      <template v-else>
        <CTableWrapper
          :current-page="1"
          :items-per-page="15"
          :limit="15"
          :title="$t('performance')"
          :data="botPer"
          :head="botPerformanceHead"
          th-class="!text-left first:!w-2"
          :loading="loading"
          no-search
          body-tr-class="transition-all hover:bg-transparent !cursor-default"
        >
          <template #afterSearch v-if="bots?.length">
            <FSelect
              :options="bots"
              v-model="botId"
              label-key="strategy"
              value-key="id"
              :placeholder="$t('choose_bot')"
            />
          </template>
          <template #_index="{ row: data }">
            <span class="font-semibold">{{ data?._index }}</span>
          </template>
          <template #profit="{ row: data }">
            <span>{{ formatMoneyExact(data?.profit, 2) }}</span>
          </template>
          <template #profit_abs="{ row: data }">
            <span class="text-dark-300">
              {{
                formatMoneyDecimal(Number(data?.profit_abs) ?? 0, 4).split(
                  ".",
                )?.[0]
              }}<span class="text-gray-380" v-if="data?.profit_abs"
                >.{{
                  formatMoneyDecimal(Number(data?.profit_abs) ?? 0, 4).split(
                    ".",
                  )?.[1]
                }}
              </span>
            </span>
          </template>
          <template #pair="{ row: data }">
            <CCardCoin without-link :card="data" :pair="data?.pair" />
          </template>
          <template #no-data>
            <CNodata
              main-class="min-h-[202px] max-w-[300px]"
              :title="$t('no_bots')"
              :subtitle="$t('no_bots_text')"
              image="/images/svg/no-data/no-bots.svg"
            />
          </template>
        </CTableWrapper>
      </template>
    </CCard>
  </Transition>
</template>

<script setup lang="ts">
import { formatMoneyExact, getObjectWithHighestTotalOrders } from "@/utils";
import { botPerformanceHead } from "@/modules/Bots/data";
import CTableWrapper from "@/components/Common/Table/CTableWrapper.vue";
import CCard from "@/components/Card/CardWrapper.vue";
import CNodata from "@/components/Common/CNodata.vue";
import { computed, ref, watch } from "vue";
import ApiService from "@/services/ApiService";
import FSelect from "@/components/Form/Select/FSelect.vue";
import { formatMoneyDecimal } from "@/utils";
import { useBotStore } from "@/modules/Bots/stores";
import CCardCoin from "@/modules/Bots/components/CCardCoin.vue";
import CPerformanceCard from "@/modules/Dashboard/components/CPerformanceCard.vue";
import CTableHeader from "@/components/Common/Table/CTableHeader.vue";
import { useMobile } from "@/composables/useMobile";

const loading = ref(true);
const botId = ref("");
const botPer = ref([]);
const botStore = useBotStore();

const { isMobile } = useMobile("mobile");

const bots = computed(() => botStore.performanceBots);

function getBotPerformance() {
  loading.value = true;
  ApiService.get(`/bot/${botId.value}/performance/`)
    .then((res) => {
      botPer.value = res?.data?.results;
    })
    .finally(() => {
      loading.value = false;
    });
}
watch(
  () => botId.value,
  () => {
    getBotPerformance();
  },
);
botStore
  .getBots({ has_performance: true })
  .then((res) => {
    if (res && res?.length) {
      botId.value = getObjectWithHighestTotalOrders(res)?.id;
      getBotPerformance();
    }
  })
  .finally(() => {
    loading.value = false;
  });
</script>

<style scoped></style>
